import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Users, BrainCircuit, Phone, PhoneOff, MessageSquare, CheckCircle2, AlertTriangle, Search, Send, FileText, Clock, Zap } from "lucide-react";

const typeIcon = {
  reason: BrainCircuit,
  action: CheckCircle2,
  call: Phone,
  call_end: PhoneOff,
  sms: MessageSquare,
  check: Search,
  send: Send,
  file: FileText,
  wait: Clock,
  warn: AlertTriangle,
  result: Zap,
};

const typeStyle = {
  reason: "bg-blue-50 border-blue-100 text-blue-700",
  action: "text-slate-700",
  call: "bg-emerald-50 border-emerald-100 text-emerald-700",
  call_end: "text-slate-500",
  sms: "bg-sky-50 border-sky-100 text-sky-700",
  check: "text-slate-600",
  send: "text-slate-600",
  file: "text-slate-600",
  wait: "text-amber-600",
  warn: "bg-amber-50 border-amber-100 text-amber-700",
  result: "bg-emerald-50 border-emerald-100 text-emerald-700 font-semibold",
};

const allSteps = [
  { phase: "Phone", text: "Answer Phone", workflow: [
    { type: "action", text: "Picks up every call within 2 rings — new patients, existing, emergencies, after-hours" },
    { type: "check", text: "Instantly matches caller ID against entire patient database" },
    { type: "reason", text: "Identifies caller intent from first sentence — scheduling, billing question, emergency, refill, etc." },
    { type: "action", text: "Pulls up full patient record while talking — history, insurance, balance, open treatment" },
    { type: "reason", text: "Adapts tone and pace based on caller's emotional state — rushed, anxious, confused, frustrated" },
    { type: "action", text: "Routes to correct workflow — booking, triage, transfer, or answers question directly" },
    { type: "action", text: "Handles multi-language callers, hard-of-hearing patients, and background noise" },
    { type: "action", text: "If new patient, starts intake flow on the call — captures name, DOB, insurance, reason for visit" },
    { type: "result", text: "Every call answered, every caller helped — 24/7/365, no hold music, no voicemail black hole" },
  ]},
  { phase: "Phone", text: "Look Up Patient in PMS", workflow: [
    { type: "action", text: "Queries PMS in real-time — pulls demographics, insurance, ledger, Tx history" },
    { type: "check", text: "Reviews visit history — last cleaning, last X-rays, last treatment dates" },
    { type: "check", text: "Checks insurance status — carrier, plan type, remaining benefits, deductible status" },
    { type: "check", text: "Pulls open treatment plans — what's been diagnosed, accepted, and unscheduled" },
    { type: "check", text: "Reviews clinical notes — allergies, medical conditions, provider preferences" },
    { type: "warn", text: "Flags outstanding balances, overdue recalls, or expired insurance" },
    { type: "action", text: "Surfaces all relevant context before the patient finishes their first sentence" },
    { type: "result", text: "Full patient picture available instantly — no \"let me put you on hold to check\"" },
  ]},
  { phase: "Phone", text: "Check Schedule Availability", workflow: [
    { type: "reason", text: "Determines procedure type, required provider, operatory, and time block needed" },
    { type: "check", text: "Cross-references provider schedules, operatory availability, and equipment needs" },
    { type: "check", text: "Factors in patient preferences — morning vs afternoon, specific days, specific provider" },
    { type: "reason", text: "Evaluates production impact of each slot — prioritizes high-value procedures in prime time" },
    { type: "check", text: "Accounts for buffer time between procedures, sterilization turnaround, lab delivery dates" },
    { type: "action", text: "Identifies optimal slot that balances patient convenience with practice production" },
    { type: "action", text: "Checks if related procedures can be batched to reduce patient visits" },
    { type: "result", text: "Best available slot found in seconds — factoring in 6+ variables simultaneously" },
  ]},
  { phase: "Phone", text: "Book Appointment", workflow: [
    { type: "action", text: "Creates appointment in PMS with correct procedure codes, provider, operatory, and duration" },
    { type: "action", text: "Links appointment to existing treatment plan if applicable" },
    { type: "check", text: "Validates no double-booking, equipment conflicts, or provider time-off" },
    { type: "action", text: "Sets clinical alerts — allergies, premedication needs, special equipment requirements" },
    { type: "action", text: "Updates waitlist — removes patient from queue, promotes next candidate" },
    { type: "action", text: "Blocks buffer time for lab work, CEREC milling, or multi-step procedures" },
    { type: "result", text: "Appointment fully configured in PMS — treatment plan linked, alerts set, waitlist updated" },
  ]},
  { phase: "Phone", text: "Send Confirmation", workflow: [
    { type: "sms", text: "Sends confirmation text with date, time, provider, and reply-to-confirm prompt" },
    { type: "send", text: "Sends confirmation email with full details, directions, parking info" },
    { type: "action", text: "Generates and attaches calendar invite (.ics) for automatic phone calendar sync" },
    { type: "action", text: "Includes pre-visit checklist — what to bring, arrival time, any prep instructions" },
    { type: "action", text: "Attaches digital intake forms link if any forms are incomplete" },
    { type: "action", text: "Schedules cascading reminders — 48h text + email, 24h text, 2h text" },
    { type: "action", text: "Monitors for confirmation reply — logs response and updates PMS status" },
    { type: "result", text: "Multi-channel confirmation deployed with auto-reminders — zero manual follow-up" },
  ]},
  { phase: "Phone", text: "Call Patient to Confirm", workflow: [
    { type: "action", text: "Identifies all patients needing confirmation — 48h and 24h before their appointment" },
    { type: "sms", text: "Texts each patient first — \"Reply Y to confirm your appointment\"" },
    { type: "check", text: "Monitors for text responses — logs confirmations, flags non-responders" },
    { type: "call", text: "Calls patients who haven't responded to text — carries out natural confirmation conversation" },
    { type: "call_end", text: "If no answer, leaves voicemail and sends follow-up text" },
    { type: "reason", text: "If patient says they can't make it, pivots to rescheduling conversation — finds new slot on the call" },
    { type: "action", text: "If patient needs to cancel, handles cancellation and immediately triggers waitlist fill workflow" },
    { type: "action", text: "Updates PMS status for every patient — Confirmed, Rescheduled, or Cancelled" },
    { type: "wait", text: "Retries non-responders on escalating cadence until appointment time" },
    { type: "result", text: "Every patient contacted, every status updated — cancellations caught early so gaps can be filled" },
  ]},
  { phase: "Phone", text: "Call Waitlist to Fill Cancellations", workflow: [
    { type: "warn", text: "Cancellation detected — immediately calculates revenue at risk for the open slot" },
    { type: "reason", text: "Queries waitlist for patients who match: procedure type, provider, time slot, insurance active" },
    { type: "action", text: "Ranks candidates by treatment urgency, insurance year-end proximity, and contact recency" },
    { type: "sms", text: "Texts top candidates simultaneously — \"We have an opening on [date/time]. Want it? Reply YES\"" },
    { type: "check", text: "Monitors text responses — first YES wins the slot" },
    { type: "call", text: "Calls candidates who haven't responded to text — offers the opening, answers questions" },
    { type: "reason", text: "If patient has cost concerns, checks their insurance and provides OOP estimate on the spot" },
    { type: "action", text: "If patient needs convincing, discusses treatment urgency and benefit expiration" },
    { type: "action", text: "Books first available patient into the slot — updates PMS, sends confirmation" },
    { type: "action", text: "If slot still open, expands search to patients with flexible scheduling preferences" },
    { type: "result", text: "Works through entire candidate list via text + call until gap is filled or exhausted" },
  ]},
  { phase: "Phone", text: "Rearrange Schedule to Optimize Chair Time", workflow: [
    { type: "check", text: "Scans full schedule across all providers — identifies every gap, short block, and dead time" },
    { type: "reason", text: "Calculates production impact of each gap — prioritizes filling highest-value slots first" },
    { type: "sms", text: "Texts all patients on tomorrow's schedule to confirm appointments" },
    { type: "action", text: "Processes patient responses — confirmations, reschedule requests, cancellations" },
    { type: "call", text: "Calls patients who haven't responded to texts" },
    { type: "reason", text: "If patient says they can't make it, nudges toward rescheduling over intelligent conversation" },
    { type: "action", text: "Handles rescheduling — finds new slot, moves appointment, confirms change" },
    { type: "action", text: "Takes any slot that opens up and immediately texts waitlist patients to fill it" },
    { type: "call", text: "Calls waitlist patients who don't respond to text — offers the opening" },
    { type: "action", text: "Moves appointments to compress gaps — offers patients earlier times, consolidates blocks" },
    { type: "action", text: "Balances hygiene vs restorative blocks to maximize production per hour" },
    { type: "action", text: "Identifies chronic no-show patients and double-books as backup" },
    { type: "sms", text: "Sends updated confirmations to every patient whose time changed" },
    { type: "result", text: "Schedule kept tight — every gap attacked, every change confirmed, production maximized" },
  ]},

  { phase: "Pre-Visit", text: "Send Intake Forms", workflow: [
    { type: "check", text: "Identifies which forms each patient needs — medical history, dental history, insurance, consent, HIPAA" },
    { type: "sms", text: "Sends personalized text with digital forms link — mobile-friendly, auto-saves progress" },
    { type: "send", text: "Sends email backup with same link + PDF fallback option" },
    { type: "check", text: "Tracks completion status in real-time — which forms done, which still pending" },
    { type: "sms", text: "Sends timed reminders for incomplete forms — 48h, 24h, same-day" },
    { type: "call", text: "Calls patients with outstanding forms — walks them through completion over the phone" },
    { type: "action", text: "For patients who struggle with digital, captures info verbally and fills forms on their behalf" },
    { type: "result", text: "100% form completion before arrival — zero front-desk bottleneck at check-in" },
  ]},
  { phase: "Pre-Visit", text: "Chase Down Missing Forms", workflow: [
    { type: "check", text: "Scans tomorrow's patient list — flags every patient with incomplete or missing forms" },
    { type: "reason", text: "Prioritizes by clinical urgency — medical history for patients with conditions, consent for procedures" },
    { type: "sms", text: "Sends targeted text to each patient — specific form name, why it's needed, direct link" },
    { type: "wait", text: "Monitors for completions — tracks who opened the link, who started, who finished" },
    { type: "call", text: "Calls non-responders — explains what's missing, offers to complete over the phone" },
    { type: "action", text: "If patient can't complete digitally, captures responses verbally and enters into system" },
    { type: "sms", text: "Sends final same-day reminder to any remaining holdouts" },
    { type: "call", text: "Last-resort call for critical forms — procedure can't happen without consent" },
    { type: "action", text: "Logs every touchpoint for compliance — who was contacted, when, outcome" },
    { type: "result", text: "Follows up relentlessly through text, email, and call until every form is complete" },
  ]},
  { phase: "Pre-Visit", text: "Enter Intake Info into PMS", workflow: [
    { type: "action", text: "Parses completed forms — extracts structured data from free-text and checkbox responses" },
    { type: "action", text: "Maps demographics into PMS fields — name, DOB, address, phone, email, emergency contact" },
    { type: "action", text: "Enters medical history — conditions, medications, allergies, surgical history" },
    { type: "check", text: "Cross-references insurance info from form against scanned card — validates member ID, group #" },
    { type: "warn", text: "Flags any discrepancies between form data and existing records for front desk review" },
    { type: "action", text: "Sets clinical alerts — allergy flags, premedication requirements, medical conditions" },
    { type: "action", text: "Uploads signed consent forms and HIPAA acknowledgment to document store" },
    { type: "result", text: "Every field populated, validated, and flagged — what takes front desk 15 min done in seconds" },
  ]},
  { phase: "Pre-Visit", text: "Scan Insurance Cards & ID", workflow: [
    { type: "action", text: "Processes uploaded insurance card images — front and back" },
    { type: "action", text: "OCR extracts carrier, plan type, member ID, group #, subscriber info, effective date" },
    { type: "action", text: "Processes state ID — extracts name, DOB, address for identity verification" },
    { type: "check", text: "Matches extracted data against patient record — validates name, DOB, address" },
    { type: "action", text: "Updates PMS with current insurance info — carrier, plan, member ID, group #" },
    { type: "warn", text: "If insurance changed since last visit, archives old plan and configures new one" },
    { type: "action", text: "Triggers eligibility check automatically with new insurance info" },
    { type: "result", text: "Cards processed, data extracted, PMS updated — no manual data entry" },
  ]},
  { phase: "Pre-Visit", text: "Run Eligibility Check", workflow: [
    { type: "action", text: "Submits real-time 270/271 eligibility request to the patient's insurance carrier" },
    { type: "check", text: "Verifies plan is active — confirms effective date, termination status" },
    { type: "check", text: "Pulls annual maximum, amount used YTD, and remaining balance" },
    { type: "check", text: "Pulls deductible amount, how much has been met, family vs individual" },
    { type: "check", text: "Maps coverage percentages by category — preventive, basic, major, endo, perio, ortho" },
    { type: "check", text: "Checks frequency limitations — X-rays, cleanings, SRP, crowns, pano" },
    { type: "action", text: "Auto-updates PMS benefit tables with all coverage details and limits" },
    { type: "reason", text: "Calculates estimated OOP for upcoming procedure based on verified benefits" },
    { type: "result", text: "Full benefit picture verified electronically — accurate estimates ready for patient presentation" },
  ]},
  { phase: "Pre-Visit", text: "Call Insurance for Benefits Breakdown", workflow: [
    { type: "reason", text: "Electronic eligibility returned incomplete data — need CDT-level coverage details from a live rep" },
    { type: "call", text: "Dials insurance provider line — navigates automated IVR menu system" },
    { type: "wait", text: "Holds on the line for 20-45 minutes — AI waits so your staff doesn't have to" },
    { type: "call", text: "Connects with rep — verifies coverage for each specific CDT code on the treatment plan" },
    { type: "check", text: "Asks about downgrades, exclusions, waiting periods, missing tooth clauses" },
    { type: "check", text: "Confirms frequency limitations — when each procedure was last done, when eligible again" },
    { type: "check", text: "Verifies pre-auth requirements for upcoming procedures" },
    { type: "action", text: "Documents every detail — reference number, rep name, date, exact coverage responses" },
    { type: "action", text: "Updates PMS with CDT-specific coverage, limitations, and call reference" },
    { type: "result", text: "Complete benefits breakdown captured autonomously — the call your staff dreads most, done by AI" },
  ]},
  { phase: "Pre-Visit", text: "Enter Coverage Details into PMS", workflow: [
    { type: "action", text: "Maps CDT-level coverage from eligibility check and insurance call into PMS benefit tables" },
    { type: "action", text: "Sets coverage percentages by category — preventive, basic, major, endo, perio" },
    { type: "action", text: "Configures contracted fee schedule for in-network plans" },
    { type: "action", text: "Enters frequency limitations — prophy, BWX, pano, SRP, crowns per tooth" },
    { type: "action", text: "Configures downgrade rules, age limitations, and plan-specific exclusions" },
    { type: "action", text: "Sets up annual max tracking, deductible tracking, and carryover rules" },
    { type: "check", text: "Cross-references with prior EOBs to validate accuracy" },
    { type: "result", text: "Complete insurance profile configured — any future treatment estimate will be accurate" },
  ]},
  { phase: "Pre-Visit", text: "Submit Pre-Authorization", workflow: [
    { type: "reason", text: "Identifies which procedures on the treatment plan require pre-authorization per payer rules" },
    { type: "action", text: "Compiles clinical documentation — narratives, X-rays, perio charts, photos" },
    { type: "file", text: "Generates clinical narrative justifying medical necessity for each procedure" },
    { type: "action", text: "Attaches all required supporting documentation — radiographs, clinical photos, charting" },
    { type: "send", text: "Submits pre-auth electronically to insurance carrier — generates tracking number" },
    { type: "check", text: "Monitors approval status daily — checks payer portal automatically" },
    { type: "warn", text: "If payer requests additional documentation, gathers and resubmits same day" },
    { type: "call", text: "If delayed beyond normal timeframe, calls payer to escalate and get status" },
    { type: "action", text: "Once approved, updates treatment plan with authorized amounts and patient OOP" },
    { type: "result", text: "Pre-auth tracked from submission to approval — delays caught and escalated proactively" },
  ]},

  { phase: "Day-Of", text: "Check In Patient", workflow: [
    { type: "check", text: "Verifies patient identity and scans insurance card for any changes" },
    { type: "check", text: "Confirms demographics are current — address, phone, email, emergency contact" },
    { type: "check", text: "Validates insurance is still active since last eligibility check" },
    { type: "warn", text: "Flags any outstanding balances — collects past-due amounts" },
    { type: "check", text: "Verifies all required forms are signed — procedure consent, HIPAA, financial policy" },
    { type: "action", text: "Confirms today's treatment plan and estimated patient responsibility" },
    { type: "action", text: "Collects estimated copay or sets up payment arrangement" },
    { type: "action", text: "Marks patient as checked in — notifies clinical team patient is ready" },
    { type: "result", text: "Complete check-in — identity verified, insurance confirmed, balance collected, team notified" },
  ]},
  { phase: "Day-Of", text: "Present Treatment Plan", workflow: [
    { type: "action", text: "Generates patient-friendly treatment explanation from clinical CDT codes" },
    { type: "action", text: "Translates dental jargon — \"D2740\" becomes \"porcelain crown to protect your cracked tooth\"" },
    { type: "check", text: "Calculates precise insurance estimate — what's covered, what patient owes" },
    { type: "check", text: "Verifies pre-auth status if applicable — confirms approved amount" },
    { type: "reason", text: "Evaluates remaining annual benefits — advises on timing to maximize coverage" },
    { type: "action", text: "Presents clear financial breakdown — insurance portion, patient portion, any write-offs" },
    { type: "action", text: "Offers payment options — pay today, split payments, CareCredit, in-house payment plan" },
    { type: "action", text: "Answers patient questions using chart context, insurance details, and clinical notes" },
    { type: "result", text: "Treatment plan presented with full financial transparency — patient understands what and why" },
  ]},
  { phase: "Day-Of", text: "Collect Copay", workflow: [
    { type: "check", text: "Calculates patient responsibility based on verified insurance benefits and contracted rates" },
    { type: "check", text: "Checks for existing credits, prepayments, or active payment plans" },
    { type: "action", text: "Processes payment — card on file, tap-to-pay, text-to-pay link, or payment plan setup" },
    { type: "action", text: "Generates and sends receipt via text and email" },
    { type: "action", text: "Posts payment to patient ledger — links to today's procedure codes" },
    { type: "action", text: "Updates account balance in real-time" },
    { type: "result", text: "Payment collected and posted instantly — clean ledger, zero AR risk on this visit" },
  ]},
  { phase: "Day-Of", text: "Clinical Documentation (AI Scribe)", workflow: [
    { type: "action", text: "Listens to provider during procedure — captures clinical narrative in real-time" },
    { type: "action", text: "Documents anesthesia type, dosage, injection site, and technique" },
    { type: "action", text: "Records procedure details — materials used, tooth prep, impressions, temporaries" },
    { type: "action", text: "Auto-codes CDT codes based on what was dictated — with correct modifiers" },
    { type: "check", text: "Cross-references payer bundling rules — ensures codes won't get denied" },
    { type: "action", text: "Attaches clinical photos, X-rays, and intraoral images to the note" },
    { type: "action", text: "Generates compliant clinical narrative that meets payer documentation requirements" },
    { type: "result", text: "Complete clinical record — coded, documented, photo-attached — provider never touched a keyboard" },
  ]},
  { phase: "Day-Of", text: "Generate Claim from Notes", workflow: [
    { type: "action", text: "Converts clinical note into clean claim format automatically" },
    { type: "action", text: "Maps procedures to correct CDT codes with appropriate modifiers" },
    { type: "action", text: "Calculates fees based on contracted rate or UCR schedule" },
    { type: "action", text: "Generates and attaches required clinical narratives for each procedure" },
    { type: "action", text: "Links pre-authorization numbers where applicable" },
    { type: "action", text: "Attaches supporting documentation — X-rays, photos, perio charts" },
    { type: "check", text: "Validates provider NPI, tax ID, place of service, rendering provider" },
    { type: "result", text: "Clean claim generated with all supporting docs — ready for scrub and submission" },
  ]},
  { phase: "Day-Of", text: "Scrub Claim for Errors", workflow: [
    { type: "check", text: "Runs claim through payer-specific validation rules — bundling, frequency, tooth eligibility" },
    { type: "check", text: "Validates CDT codes are current and appropriate for the tooth/quadrant" },
    { type: "check", text: "Checks frequency limitations — when this procedure was last done on this tooth" },
    { type: "check", text: "Verifies fee schedule — amounts match contracted rates for this payer" },
    { type: "check", text: "Validates patient eligibility is still active" },
    { type: "check", text: "Checks for common denial triggers — missing narratives, wrong modifiers, unbundling errors" },
    { type: "warn", text: "Flags any issues found — fixes automatically or routes to biller for review" },
    { type: "action", text: "Corrects coding errors, adds missing modifiers, attaches missing narratives" },
    { type: "result", text: "Every claim scrubbed against payer rules before submission — maximizes first-pass acceptance" },
  ]},
  { phase: "Day-Of", text: "Submit Claim", workflow: [
    { type: "send", text: "Submits claim electronically via clearinghouse to the correct payer" },
    { type: "check", text: "Receives clearinghouse acknowledgment — confirms claim format accepted" },
    { type: "check", text: "Monitors for payer receipt confirmation — claim entered into adjudication" },
    { type: "warn", text: "If rejected at clearinghouse, identifies error and resubmits immediately" },
    { type: "action", text: "Sets automated follow-up schedule — status checks at 7, 14, 21, 30 days" },
    { type: "action", text: "Logs submission with tracking IDs in PMS" },
    { type: "result", text: "Claim submitted, confirmed, and tracked — automated follow-up begins" },
  ]},
  { phase: "Day-Of", text: "Check Out Patient", workflow: [
    { type: "action", text: "Determines next appointment needed — follow-up, continuation, or recall" },
    { type: "action", text: "Finds and books next appointment while patient is still there" },
    { type: "action", text: "Generates procedure-specific post-op instructions" },
    { type: "sms", text: "Texts post-op care instructions and next appointment confirmation to patient" },
    { type: "check", text: "Checks for any referrals needed — sends referral letters if applicable" },
    { type: "action", text: "Updates recall schedule — next hygiene, next X-rays, next perio maintenance" },
    { type: "action", text: "Generates visit summary — procedures done, payments made, next steps" },
    { type: "result", text: "Patient checked out with next visit booked, instructions sent, and recall updated" },
  ]},

  { phase: "Post-Visit", text: "Track Claim Status", workflow: [
    { type: "check", text: "Automatically checks claim status at 7, 14, 21, and 30 day intervals" },
    { type: "check", text: "Monitors payer portal for status changes — received, in review, pending, paid, denied" },
    { type: "warn", text: "If payer requests additional documentation, gathers and uploads same day" },
    { type: "call", text: "If claim stalls beyond expected timeframe, calls payer to inquire and escalate" },
    { type: "action", text: "When claim pays, downloads ERA/EOB and queues for payment posting" },
    { type: "warn", text: "If denied, immediately categorizes denial reason and routes to appeal workflow" },
    { type: "action", text: "Logs every status change with timestamps in PMS" },
    { type: "result", text: "Every claim tracked from submission to payment — nothing falls through the cracks" },
  ]},
  { phase: "Post-Visit", text: "Post EOBs", workflow: [
    { type: "action", text: "Receives ERA/EOB electronically — matches to original claim in PMS" },
    { type: "check", text: "Verifies each line item — allowed amount, insurance paid, patient responsibility" },
    { type: "check", text: "Compares actual payment against expected based on verified benefits" },
    { type: "warn", text: "Flags any unexpected adjustments — downgrades, write-offs, or benefit changes" },
    { type: "action", text: "Posts insurance payment to correct patient ledger and procedure" },
    { type: "action", text: "Calculates remaining patient balance — generates patient statement if needed" },
    { type: "check", text: "Reconciles contractual adjustments — in-network write-offs match contracted rates" },
    { type: "result", text: "EOB posted, payments matched, patient balance calculated — ledger accurate to the penny" },
  ]},
  { phase: "Post-Visit", text: "Reconcile Payment to Deposit", workflow: [
    { type: "check", text: "Matches all PMS payments against bank deposits daily" },
    { type: "check", text: "Reconciles insurance EFT payments against ERA records" },
    { type: "check", text: "Reconciles patient credit card transactions against processor reports" },
    { type: "warn", text: "Identifies any discrepancies — missing payments, declined transactions posted as paid, amount mismatches" },
    { type: "reason", text: "Investigates root cause of each discrepancy — processor error, PMS entry mistake, returned payment" },
    { type: "action", text: "Corrects erroneous postings in PMS — reverses, adjusts, or reapplies as needed" },
    { type: "sms", text: "Notifies patients about failed payments with text-to-pay link to resolve" },
    { type: "result", text: "Every dollar accounted for — discrepancies found, investigated, and resolved daily" },
  ]},
  { phase: "Post-Visit", text: "Identify Underpayments", workflow: [
    { type: "check", text: "Compares every EOB payment against contracted fee schedule" },
    { type: "check", text: "Calculates exact shortfall for any claim paid below contracted rate" },
    { type: "reason", text: "Analyzes reason for underpayment — incorrect fee schedule, wrong plan tier, erroneous downgrade" },
    { type: "check", text: "Verifies current plan documents to confirm downgrades and exclusions are actually valid" },
    { type: "warn", text: "Flags incorrect downgrades — some plans remove these clauses on renewal but payers keep applying them" },
    { type: "action", text: "Submits reprocessing requests with contracted rate documentation for each underpayment" },
    { type: "action", text: "Tracks reprocessing through resolution — follows up if not corrected" },
    { type: "result", text: "Catches underpayments most offices miss entirely — recovers revenue that would otherwise be lost" },
  ]},
  { phase: "Post-Visit", text: "Appeal Denied Claims", workflow: [
    { type: "warn", text: "Receives denial — categorizes by reason code (medical necessity, frequency, bundling, etc.)" },
    { type: "reason", text: "Determines appeal strategy based on denial type — what documentation will overturn it" },
    { type: "check", text: "Pulls supporting clinical evidence — perio charts, X-rays, photos, clinical notes" },
    { type: "file", text: "Drafts appeal letter with clinical justification and evidence references" },
    { type: "action", text: "Attaches all supporting documentation — radiographs, perio charts, clinical guidelines" },
    { type: "action", text: "References AAP/ADA clinical guidelines when applicable to strengthen the case" },
    { type: "send", text: "Submits appeal through payer portal — generates tracking number" },
    { type: "check", text: "Monitors appeal status — follows up if not resolved within expected timeframe" },
    { type: "result", text: "Full clinical case built and submitted autonomously — recovers revenue most offices write off" },
  ]},
  { phase: "Post-Visit", text: "Re-Submit Rejected Claims", workflow: [
    { type: "warn", text: "Rejection detected at clearinghouse or payer — identifies specific error code" },
    { type: "reason", text: "Diagnoses root cause — invalid subscriber ID, wrong NPI, coding error, missing info" },
    { type: "check", text: "Checks patient record for updated info — new insurance card, corrected member ID" },
    { type: "action", text: "Corrects the identified error in the claim" },
    { type: "action", text: "Resubmits corrected claim same day — no revenue cycle delay" },
    { type: "check", text: "Verifies acceptance after resubmission — confirms claim entered adjudication" },
    { type: "result", text: "Rejection caught, diagnosed, corrected, and resubmitted within minutes" },
  ]},
  { phase: "Post-Visit", text: "Follow Up on Patient Balances", workflow: [
    { type: "check", text: "Scans all patient ledgers — identifies every outstanding balance by aging bucket" },
    { type: "action", text: "Categorizes by aging: 0-30, 31-60, 61-90, 90+ days — prioritizes by amount and age" },
    { type: "sms", text: "Sends friendly payment reminders via text with one-tap text-to-pay link" },
    { type: "send", text: "Emails statement with balance breakdown and online payment portal link" },
    { type: "call", text: "Calls patients with balances over 30 days — discusses balance with full context" },
    { type: "reason", text: "If patient disputes balance, pulls EOB and explains insurance vs patient responsibility" },
    { type: "action", text: "If patient can't pay in full, sets up payment plan with auto-pay" },
    { type: "action", text: "For severely aged balances, offers courtesy discount to resolve immediately" },
    { type: "action", text: "Escalates unresponsive 90+ day accounts per office collection policy" },
    { type: "result", text: "Every balance pursued through text, email, and call — payment plans set up, disputes resolved" },
  ]},
  { phase: "Post-Visit", text: "Produce Statements & Reports", workflow: [
    { type: "action", text: "Generates production reports per provider — production, collections, collection rate" },
    { type: "action", text: "Generates AR aging report — 30/60/90/120+ day buckets with dollar amounts" },
    { type: "action", text: "Calculates KPIs — case acceptance rate, hygiene recare rate, claim denial rate" },
    { type: "check", text: "Compares current period against prior periods — identifies trends up and down" },
    { type: "warn", text: "Flags concerning trends — dropping metrics, rising AR, increasing denials" },
    { type: "action", text: "Generates patient statements for all balances per office billing cycle" },
    { type: "action", text: "Delivers reports to office manager inbox on schedule" },
    { type: "result", text: "Complete reporting package — production, collections, AR, KPIs — generated automatically" },
  ]},
  { phase: "Post-Visit", text: "Work Aging AR", workflow: [
    { type: "check", text: "Pulls aging AR report — segments by 30/60/90/120+ day buckets" },
    { type: "reason", text: "Prioritizes claims by dollar value and days outstanding — highest risk first" },
    { type: "check", text: "Checks status of each aging claim on payer portal — paid, pending, denied, lost" },
    { type: "call", text: "Calls payers for claims pending 30+ days — gets status, submits missing info, escalates" },
    { type: "action", text: "For denied claims, routes to appeal workflow with denial reason and supporting docs" },
    { type: "action", text: "For \"paid but not received\" claims, requests EFT trace and reissue" },
    { type: "action", text: "Batch-submits status inquiries for all remaining aging claims" },
    { type: "result", text: "Every aging claim actively worked — statuses updated, escalations filed, appeals triggered" },
  ]},
  { phase: "Post-Visit", text: "Handle Billing Inquiries", workflow: [
    { type: "call", text: "Answers patient call about a bill — understands the question, pulls up their account" },
    { type: "check", text: "Pulls patient ledger, EOBs, treatment history — full context in seconds" },
    { type: "action", text: "Explains charges in plain language — what was done, what insurance paid, what patient owes" },
    { type: "action", text: "Walks through EOB line by line if patient wants detail" },
    { type: "reason", text: "If patient disputes a charge, investigates — checks for posting errors, insurance miscalculation" },
    { type: "action", text: "If patient needs payment plan, sets one up on the spot with auto-pay" },
    { type: "sms", text: "Sends payment confirmation, statement copy, or EOB explanation via text after the call" },
    { type: "result", text: "Billing inquiry resolved in one call — no transfers, no callbacks, no confused patients" },
  ]},
  { phase: "Post-Visit", text: "File Faxes & Documents", workflow: [
    { type: "check", text: "Receives and scans all incoming faxes — classifies each document type automatically" },
    { type: "action", text: "Identifies EOBs — matches to patient claims, routes to payment posting workflow" },
    { type: "action", text: "Identifies referral letters — attaches to correct patient chart" },
    { type: "action", text: "Identifies lab results — matches to patient by name/DOB, files in records" },
    { type: "action", text: "Identifies pre-auth approvals — updates treatment plan status, notifies team" },
    { type: "action", text: "Identifies records requests — prepares and sends patient records per HIPAA" },
    { type: "warn", text: "Flags illegible or unidentifiable documents for manual review" },
    { type: "result", text: "Every fax auto-classified, routed, and filed — what takes staff 45 min done in 2" },
  ]},

  { phase: "Ongoing", text: "Hygiene Recall Outreach", workflow: [
    { type: "check", text: "Scans entire patient database — identifies every patient overdue for hygiene recall" },
    { type: "action", text: "Segments by how overdue — 0-2 months, 3-6 months, 6-12 months, 12+ months" },
    { type: "sms", text: "Texts recent overdue patients with booking link — \"You're due for your cleaning!\"" },
    { type: "send", text: "Emails moderately overdue patients with \"we miss you\" outreach + online booking" },
    { type: "call", text: "Calls significantly overdue patients — personal conversation about why they should come back" },
    { type: "reason", text: "If patient has objections (cost, fear, busy), addresses each one with context from their chart" },
    { type: "action", text: "Checks insurance benefits in real-time — tells patient if cleaning is covered at 100%" },
    { type: "action", text: "Books appointment on the call if patient agrees — confirms and sends reminders" },
    { type: "sms", text: "Runs drip campaigns for non-responders — weekly touchpoints for 4 weeks before marking inactive" },
    { type: "result", text: "Entire recall list worked systematically — text, email, call — until scheduled or opted out" },
  ]},
  { phase: "Ongoing", text: "Unscheduled Treatment Follow-Up", workflow: [
    { type: "check", text: "Pulls all diagnosed but unscheduled treatment across the practice — ranked by value and urgency" },
    { type: "reason", text: "Prioritizes by clinical urgency, dollar amount, and insurance year-end proximity" },
    { type: "sms", text: "Texts patients with personalized message about their specific unscheduled treatment" },
    { type: "call", text: "Calls high-value patients — discusses their treatment, answers questions, addresses concerns" },
    { type: "reason", text: "If patient cites cost concern, checks their insurance benefits and calculates true OOP" },
    { type: "action", text: "Presents financing options — payment plans, CareCredit, benefit maximization strategies" },
    { type: "reason", text: "If patient cites fear/anxiety, discusses sedation options and comfort amenities" },
    { type: "action", text: "If patient agrees, books appointment on the call — links to treatment plan in PMS" },
    { type: "action", text: "Tracks every outreach attempt and patient response — escalates stalled high-value cases" },
    { type: "result", text: "Systematically pursues every dollar of unscheduled treatment — overcomes objections, books appointments" },
  ]},
  { phase: "Ongoing", text: "Case Acceptance Calls", workflow: [
    { type: "check", text: "Identifies all patients who were presented treatment in last 90 days but didn't schedule" },
    { type: "reason", text: "Analyzes deferral patterns — categorizes by cost concern, fear, scheduling, or undecided" },
    { type: "call", text: "Calls cost-concern patients — presents updated financial options and benefit reminders" },
    { type: "action", text: "Calculates remaining benefits and year-end urgency — \"your benefits expire Dec 31\"" },
    { type: "call", text: "Calls fear/anxiety patients — empathy-first approach with sedation and comfort options" },
    { type: "sms", text: "Texts scheduling-issue patients with online booking link and flexible hour options" },
    { type: "action", text: "If patient re-engages, books appointment on the spot and sends confirmation" },
    { type: "action", text: "Tracks re-engagement rates by deferral category — refines approach over time" },
    { type: "result", text: "Re-engages patients who said no — recovers lost production through persistent, personalized outreach" },
  ]},
  { phase: "Ongoing", text: "Verify Remaining Benefits Mid-Year", workflow: [
    { type: "check", text: "Runs benefits analysis across all active patients — pulls remaining maximums and unused benefits" },
    { type: "action", text: "Identifies patients with significant remaining benefits AND unscheduled treatment" },
    { type: "reason", text: "Prioritizes patients whose benefits reset Jan 1 — use-it-or-lose-it opportunity" },
    { type: "sms", text: "Texts patients with their specific remaining benefit amount and a booking link" },
    { type: "call", text: "Calls highest-value patients — explains benefit expiration, calculates OOP for their treatment" },
    { type: "action", text: "Shows patients exactly how much they'd save by scheduling before year-end" },
    { type: "action", text: "Books appointments for patients who agree — maximizes benefit utilization" },
    { type: "result", text: "Captures expiring benefits before they reset — production that would otherwise vanish on Jan 1" },
  ]},
  { phase: "Ongoing", text: "Patient Satisfaction & Reviews", workflow: [
    { type: "sms", text: "Sends post-visit satisfaction survey via text — simple 1-5 rating with optional comment" },
    { type: "check", text: "Monitors responses in real-time — categorizes by rating" },
    { type: "sms", text: "Routes happy patients (4-5 stars) to Google Review — \"Would you share your experience?\"" },
    { type: "warn", text: "Flags unhappy patients (1-2 stars) — escalates immediately to office manager" },
    { type: "reason", text: "Analyzes negative feedback — identifies issue category (wait time, pain, billing, communication)" },
    { type: "action", text: "Drafts personalized service recovery message for manager approval" },
    { type: "sms", text: "Sends approved apology message with service recovery offer" },
    { type: "action", text: "Tracks satisfaction trends over time — identifies systemic issues before they become patterns" },
    { type: "result", text: "Generates Google reviews from happy patients, catches unhappy patients before they post negative reviews" },
  ]},
  { phase: "Ongoing", text: "Referral & Records Management", workflow: [
    { type: "check", text: "Scans treatment notes for referrals — identifies every specialist referral generated" },
    { type: "file", text: "Generates referral letters with clinical notes, X-rays, and relevant history" },
    { type: "send", text: "Sends referral package to specialist office via fax, portal, or secure email" },
    { type: "sms", text: "Texts patient with specialist info, phone number, and scheduling instructions" },
    { type: "action", text: "For incoming referrals, downloads records and creates patient profile" },
    { type: "check", text: "Follows up on pending referrals — confirms patients actually scheduled with specialists" },
    { type: "call", text: "Calls patients who haven't scheduled their referral — reminds and offers to help book" },
    { type: "action", text: "Tracks referral loop to completion — ensures no patient falls through the cracks" },
    { type: "result", text: "Every referral sent, received, and tracked to completion — zero referral leakage" },
  ]},
];

const phaseColors = {
  Phone: { dot: "bg-blue-500", text: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200", hoverBg: "bg-blue-50", pillBg: "bg-blue-50 hover:bg-blue-100", accent: "#3b82f6" },
  "Pre-Visit": { dot: "bg-indigo-500", text: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200", hoverBg: "bg-indigo-50", pillBg: "bg-indigo-50 hover:bg-indigo-100", accent: "#6366f1" },
  "Day-Of": { dot: "bg-purple-500", text: "text-purple-700", bg: "bg-purple-50", border: "border-purple-200", hoverBg: "bg-purple-50", pillBg: "bg-purple-50 hover:bg-purple-100", accent: "#a855f7" },
  "Post-Visit": { dot: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", hoverBg: "bg-red-50", pillBg: "bg-red-50 hover:bg-red-100", accent: "#ef4444" },
  Ongoing: { dot: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200", hoverBg: "bg-amber-50", pillBg: "bg-amber-50 hover:bg-amber-100", accent: "#f59e0b" },
};

const phaseGroups = Object.keys(phaseColors).map((phase) => ({
  phase,
  steps: allSteps.filter((s) => s.phase === phase),
}));

const WorkflowLine = ({ item, index }) => {
  const Icon = typeIcon[item.type] || CheckCircle2;
  const style = typeStyle[item.type] || "text-slate-600";
  const isBlock = ["reason", "call", "sms", "warn", "result"].includes(item.type);

  if (isBlock) {
    return (
      <motion.div
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: index * 0.03, duration: 0.2 }}
        className={`rounded-lg border px-3 py-2 ${style}`}
      >
        <div className="flex items-start gap-2">
          <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-70" />
          <span className="text-[11px] leading-relaxed">{item.text}</span>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03, duration: 0.2 }}
      className="flex items-start gap-2 px-1"
    >
      <Icon className="w-3 h-3 flex-shrink-0 mt-0.5 text-slate-400" />
      <span className={`text-[11px] leading-relaxed ${style}`}>{item.text}</span>
    </motion.div>
  );
};

const Popover = ({ step, colors, anchorRef, onMouseEnter, onMouseLeave }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const popW = 380;
    const popH = 420;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow > popH + 8
      ? rect.bottom + 6
      : Math.max(8, rect.top - popH - 6);
    const left = Math.min(rect.left, window.innerWidth - popW - 12);
    setPos({ top, left: Math.max(8, left) });
  }, [anchorRef]);

  return ReactDOM.createPortal(
    <motion.div
      ref={popoverRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      initial={{ opacity: 0, y: 6, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.96 }}
      transition={{ duration: 0.15 }}
      style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999 }}
      className="w-[380px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
    >
      {/* Header */}
      <div className={`px-4 py-2.5 ${colors.bg} border-b ${colors.border} flex items-center gap-2`}>
        <BrainCircuit className="w-3.5 h-3.5" style={{ color: colors.accent }} />
        <span className="text-[12px] font-bold text-slate-800 flex-1">{step.text}</span>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: colors.accent, color: 'white' }}>
          {step.workflow.length} steps
        </span>
      </div>

      {/* Workflow stream */}
      <div className="px-3 py-2.5 max-h-[360px] overflow-y-auto space-y-1.5">
        {step.workflow.map((item, i) => (
          <WorkflowLine key={i} item={item} index={i} />
        ))}
      </div>
    </motion.div>,
    document.body
  );
};

const StepPill = ({ step, colors }) => {
  const [isOpen, setIsOpen] = useState(false);
  const btnRef = useRef(null);
  const timeoutRef = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  return (
    <>
      <div
        ref={btnRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border transition-all duration-200 text-left cursor-default ${
          isOpen
            ? `${colors.bg} ${colors.border} shadow-sm`
            : `${colors.pillBg} border-transparent`
        }`}
      >
        <span className="text-[12px] font-medium text-slate-700 whitespace-nowrap">
          {step.text}
        </span>
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
          isOpen
            ? `${colors.dot} text-white`
            : "bg-slate-200/80 text-slate-500"
        }`}>
          {step.workflow.length}
        </span>
      </div>
      <AnimatePresence>
        {isOpen && (
          <Popover
            step={step}
            colors={colors}
            anchorRef={btnRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        )}
      </AnimatePresence>
    </>
  );
};

const PatientJourneySlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-5 lg:p-6 relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full min-h-0">
        {/* Header */}
        <div className="mb-2 flex-shrink-0">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold mb-1 border border-blue-100">
              <Users className="w-3.5 h-3.5" />
              <span>The Patient Journey</span>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
              Asha's <span className="text-blue-600">AI</span> performs the{" "}
              <span className="text-blue-600">work/labor</span> that takes your
              staff 3-4 hours per patient
            </h2>
            <p className="text-xs text-slate-600 leading-relaxed mt-1">
              You pay $120+ per patient journey, but work still falls through.
              AI handles all of it — accurately, 24/7. Hover any step to see the
              reasoning and actions.
            </p>
          </motion.div>
        </div>

        {/* Phase rows */}
        <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5">
          {phaseGroups.map(({ phase, steps }) => {
            const colors = phaseColors[phase];
            return (
              <div key={phase}>
                {/* Phase label */}
                <div className="flex items-center gap-2 mb-1 sticky top-0 bg-slate-50/95 backdrop-blur-sm py-0.5 z-10">
                  <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${colors.text}`}>
                    {phase}
                  </span>
                  <div className="flex-1 h-px bg-slate-200/80" />
                  <span className="text-[9px] text-slate-400">
                    {steps.length} steps
                  </span>
                </div>

                {/* Steps as pills with popover on hover */}
                <div className="flex flex-wrap items-center gap-1.5 mb-1">
                  {steps.map((step, si) => (
                    <React.Fragment key={si}>
                      <StepPill step={step} colors={colors} />
                      {si < steps.length - 1 && (
                        <ArrowRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PatientJourneySlide;
